(() => {
  const initialized = new WeakSet();

  function init(root) {
    if (!root || initialized.has(root)) return;
    initialized.add(root);

    const track = root.querySelector('.vena-pdp-core__track');
    const thumbs = Array.from(root.querySelectorAll('.vena-pdp-core__thumb'));
    const thumbRail = root.querySelector('.vena-pdp-core__thumbs');
    const thumbProgress = root.querySelector('[data-vena-pdp-thumb-progress]');
    const desktopThumbProgress = root.querySelector('[data-vena-pdp-desktop-thumb-progress]');
    const slides = track ? Array.from(track.querySelectorAll('.vena-pdp-core__slide')) : [];
    if (!track || slides.length < 2) return;

    let activeIndex = 0;
    let isProgrammaticScroll = false;
    let touchStart = null;
    let mouseStart = null;

    function updateThumbProgress() {
      if (!thumbRail) return;

      if (thumbProgress) {
        const maxScroll = Math.max(0, thumbRail.scrollWidth - thumbRail.clientWidth);
        const visible = thumbRail.scrollWidth ? Math.min(1, thumbRail.clientWidth / thumbRail.scrollWidth) : 1;
        const width = Math.max(0.18, visible);
        const ratio = maxScroll ? thumbRail.scrollLeft / maxScroll : 0;
        thumbProgress.style.width = `${width * 100}%`;
        thumbProgress.style.transform = `translateX(${ratio * ((100 / width) - 100)}%)`;
      }

      if (desktopThumbProgress) {
        const maxScroll = Math.max(0, thumbRail.scrollHeight - thumbRail.clientHeight);
        const visible = thumbRail.scrollHeight ? Math.min(1, thumbRail.clientHeight / thumbRail.scrollHeight) : 1;
        const height = Math.max(0.16, visible);
        const ratio = maxScroll ? thumbRail.scrollTop / maxScroll : 0;
        desktopThumbProgress.style.height = `${height * 100}%`;
        desktopThumbProgress.style.transform = `translateY(${ratio * ((100 / height) - 100)}%)`;
      }
    }

    function syncThumbRailHeight() {
      if (!thumbRail) return;

      if (!window.matchMedia('(min-width: 990px)').matches) {
        thumbRail.style.removeProperty('--vena-pdp-media-height');
        desktopThumbProgress?.parentElement?.style.removeProperty('--vena-pdp-media-height');
        return;
      }

      const mediaHeight = Math.round(track.getBoundingClientRect().height);
      if (mediaHeight > 0) {
        thumbRail.style.setProperty('--vena-pdp-media-height', `${mediaHeight}px`);
        desktopThumbProgress?.parentElement?.style.setProperty('--vena-pdp-media-height', `${mediaHeight}px`);
      }
    }

    function normaliseIndex(index) {
      return ((index % slides.length) + slides.length) % slides.length;
    }

    function syncActiveVideo() {
      slides.forEach((slide, index) => {
        const video = slide.querySelector('video');
        if (!video) return;

        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.controls = false;

        if (index === activeIndex) {
          const playAttempt = video.play();
          if (playAttempt && typeof playAttempt.catch === 'function') playAttempt.catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    function setActive(index) {
      activeIndex = normaliseIndex(index);
      thumbs.forEach((thumb, thumbIndex) => {
        const isActive = thumbIndex === activeIndex;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
      syncActiveVideo();
    }

    function goTo(index, behavior = 'smooth') {
      setActive(index);
      isProgrammaticScroll = true;
      track.scrollTo({ left: slides[activeIndex].offsetLeft, behavior });
      window.setTimeout(() => {
        isProgrammaticScroll = false;
      }, behavior === 'smooth' ? 550 : 60);
    }

    function settleOnActive() {
      goTo(activeIndex, 'smooth');
    }

    function finishSwipe(start, endX, endY) {
      if (!start) return;

      const deltaX = endX - start.x;
      const deltaY = endY - start.y;
      const hasHorizontalIntent = Math.abs(deltaX) >= 42 && Math.abs(deltaX) > Math.abs(deltaY) * 1.15;

      if (!hasHorizontalIntent) {
        settleOnActive();
        return;
      }

      goTo(deltaX < 0 ? activeIndex + 1 : activeIndex - 1);
    }

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', (event) => {
        event.preventDefault();
        goTo(index);
      });
    });

    /*
     * Touch events are used intentionally for mobile. They remain reliable inside
     * Shopify's theme preview and prevent a native free-scroll from skipping slides.
     * The CSS touch-action rule preserves ordinary vertical page scrolling.
     */
    track.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      touchStart = { x: touch.clientX, y: touch.clientY };
    }, { passive: true });

    track.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      if (!touch || !touchStart) return;
      finishSwipe(touchStart, touch.clientX, touch.clientY);
      touchStart = null;
    }, { passive: true });

    track.addEventListener('touchcancel', () => {
      touchStart = null;
      settleOnActive();
    }, { passive: true });

    /* Desktop mouse drag support only. Touch is handled above to avoid duplicate moves. */
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      mouseStart = { x: event.clientX, y: event.clientY };
      track.setPointerCapture?.(event.pointerId);
    });

    track.addEventListener('pointerup', (event) => {
      if (event.pointerType !== 'mouse' || !mouseStart) return;
      finishSwipe(mouseStart, event.clientX, event.clientY);
      mouseStart = null;
    });

    track.addEventListener('pointercancel', (event) => {
      if (event.pointerType !== 'mouse') return;
      mouseStart = null;
      settleOnActive();
    });

    thumbRail?.addEventListener('scroll', updateThumbProgress, { passive: true });
    window.addEventListener('resize', updateThumbProgress);
    window.addEventListener('resize', syncThumbRailHeight);

    if ('ResizeObserver' in window) {
      const galleryResizeObserver = new ResizeObserver(() => {
        syncThumbRailHeight();
        updateThumbProgress();
      });
      galleryResizeObserver.observe(track);
    }

    track.addEventListener('scroll', () => {
      if (isProgrammaticScroll) return;
      const nearest = slides.reduce((bestIndex, slide, index) => (
        Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[bestIndex].offsetLeft - track.scrollLeft) ? index : bestIndex
      ), 0);
      setActive(nearest);
    }, { passive: true });

    setActive(0);
    requestAnimationFrame(() => {
      syncThumbRailHeight();
      updateThumbProgress();
    });
  }

  function initAll(scope = document) {
    const roots = [];
    if (scope.matches?.('.vena-pdp-core')) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll('.vena-pdp-core'));
    roots.forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();